import { useState } from 'react';
import _ from 'lodash';
import actStore from '@flux/actions/actStore';
import { Grid, TextInput, Typography, Accordion, Tabs, GridContainerFullHeight } from '@ts/storybook';
import Readings, { defaultConfigComponent as RConfigComponent } from '@pages/DataBase/Readings/Readings';
import StatusInput from '@pages/DataBase/Status/StatusInput';
import Misc from '@components/Misc';
import BarcodePrinter from '@components/Utils/Printer/BarcodePrinter';
import { CElement, CElementType, EFlavors, CPerson, IForm, CStatusIndicator } from '@ts/ui-models';
import { IAppProps } from '@interfaces/App.interface';
import GTLocationElement from './GTLocationElement';
import GTXtagElement from './GTXtagElement';
import GTItemTypeInLocation, { defaultConfigComponent as GTConfigComponent } from './GTItemTypeInLocation';
import WrapperEntitySelector from '@modules/entitySelector/WrapperEntitySelector';
import Events, { defaultConfigComponent as EConfigComponent } from '@pages/DataBase/Events/Events';
import Tasks, { defaultConfigComponent as TConfigComponent } from '@pages/DataBase/Tasks/Tasks';
import GeometryDrawer from '@components/Maps/Map/GeometryDrawer';
import LocationHistoryTab from '@pages/DataBase/Flavors/Items/LocationHistoryTab';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import PersonDataAccordion from '@modules/accordions/PersonDataAccordion';
import AvatarImage from '@components/Entities/AvatarImage';

interface GeneralTabProps extends IAppProps, IForm {
  values: CElement;
}

const NewGeneralTab = (props: GeneralTabProps) => {
  console.log('props', props);
  const { values: tElement, onChange } = props;

  const [state, setState] = useState<any>({
    activeTabModule2: tElement.haveFlavor(EFlavors.LOCATION) ? 'locationFlavorLocation' : 'locationItem',
    activeTabModule3: 'status',
  });
  const isViewMode = props.mode === 'view';

  //#region Handlers

  const onChangeName = (name: string | number | null) => {
    props.onCallbackChange('name')(name);
    props.onCallbackChange('customId')(name);
  };

  const changeType = (itemType: CElementType | null) => {
    props.onCallbackChange('elementTypeId')(itemType?.getID() ?? null);
    props.onCallbackChange('elementType')(itemType);
  };

  const handleChangeEntitySelector = (entity: any, entityName: string, fkField: string) => {
    const value = entity ? entity.getID() : null;
    props.onCallbackChange(entityName)(entity);
    props.onCallbackChange(fkField)(value);
  };

  const handleLastUpdateClick = indicator => {
    const readingType = indicator.get('readingType');

    if (!readingType) return;

    const entity = getReadingTypeByIdFromStore(readingType.get('logicalId'));
    const filters = {
      stream: {
        search: {
          attr: 'readingTypeId',
          value: readingType.get('logicalId'),
        },
        filter: {
          desc: 'Reading type',
          fnValue: me => readingType.get('name'),
        },
        entity,
        scope: 'external',
      },
    };
    showItemReadings(filters);
  };

  const handleTabChange = field => (event, newValue) => {
    setState({ ...state, [field]: newValue });
  };

  const handleClearImage = () => {
    onChange(
      'documents',
      tElement.documents?.filter(image => image.purposeId !== 'default')
    );
  };

  const handleUploadPhotoSuccess = image => {
    console.log('image', image);
    let documents = tElement.documents?.filter(doc => doc.purposeId !== 'default') ?? [];
    documents?.push(image);
    onChange('documents', documents);
    /* if (loggedUserId === targetUserId) {
      dispatch(globalActions.updateGlobalState({ key: 'user', value: { ...user, defaultImage: image.locator } }));
    } */
  };
  //#endregion

  //#region Functions
  const getReadingTypeByIdFromStore = readingTypeId => {
    const readingTypes = props.data.getIn(['readingTypes', 'data']);
    return readingTypes?.find(rt => rt.get('logicalId') == readingTypeId);
  };

  const showItemReadings = (filters?: any) => {
    actStore.setActionTableV3({
      action: 'openModalReading',
      row: tElement,
      filters,
    });
  };

  const disabledAttr = attr => {
    return isViewMode || !!tElement.attrsLocked?.find(a => a === attr) || !!tElement.attrsReadOnly?.find(a => a === attr);
  };

  /**
   * Returns the list of tabs based on the given module.
   *
   * @param { 'module2' | 'module3' } module - The module to get the tabs for.
   * @returns { { label: string; value: string }[] } - The list of tabs for the given module.
   */
  const getListTabsFromItem = (module: 'module2' | 'module3'): { label: string; value: string }[] => {
    const listModule2: { label: string; value: string }[] = [
      ...(!tElement.haveFlavor(EFlavors.LOCATION)
        ? [
            { label: 'Location', value: 'locationItem' },
            ...(isViewMode ? [{ label: 'Location history', value: 'locationHistory' }] : []),
          ]
        : []),
      ...(tElement.haveFlavor(EFlavors.LOCATION) ? [{ label: 'Location', value: 'locationFlavorLocation' }] : []),
      //...(isViewMode ? [{ label: 'Metrics', value: 'itemXtag' }] : []),
    ];
    const listModule3: { label: string; value: string }[] = [
      { label: 'Status', value: 'status' },
      ...(isViewMode ? [{ label: 'Readings', value: 'readings' }] : []),
      ...(isViewMode && tElement.haveFlavor(EFlavors.LOCATION)
        ? [{ label: 'Items at this location', value: 'itemTypeInLocation' }]
        : []),
      ...(isViewMode ? [{ label: 'Events', value: 'events' }] : []),
      ...(tElement.haveFlavor(EFlavors.LOCATION) && isViewMode
        ? [{ label: 'Events involving this location', value: 'eventEnvolvingLocation' }]
        : []),
      ...(isViewMode ? [{ label: 'Tasks', value: 'tasks' }] : []),
    ];

    return module === 'module2' ? listModule2 : listModule3;
  };

  //#endregion

  //#region Render
  const renderSubModule1 = () => {
    switch (tElement.mainFlavor) {
      default:
        return (
          <AvatarImage
            mode={props.mode}
            cEntity={tElement}
            image={tElement.getAvatar()}
            onUploadedPhoto={handleUploadPhotoSuccess}
            onClearImage={handleClearImage}
            entityTypeId="tsElement"
            entityId={tElement.getID()}
          />
        );
    }
  };
  const renderFlavorComponents = () => {
    const mainFlavor = tElement.mainFlavor ?? tElement.params?.mainFlavor ?? tElement.tEntity;
    switch (mainFlavor) {
      //case 'people':
      case EFlavors.PERSON:
        const tPerson = new CPerson(tElement);
        return (
          <PersonDataAccordion
            {...props}
            idLinked={tPerson.getPersonData()?.user?.getID()}
            linkToUser
            isViewMode={isViewMode}
            personData={tPerson.getPersonData()}
          />
        );

      default:
        break;
    }
  };

  const renderAccordionPeople = () => {
    if (
      isViewMode &&
      tElement.contactPerson == null &&
      tElement.owner == null &&
      tElement.holder == null &&
      tElement.legalPerson == null
    )
      return null;

    return (
      <Accordion title="Related people" open={!isViewMode}>
        <>
          {((isViewMode && tElement.contactPerson) || !isViewMode) && (
            <WrapperEntitySelector
              label="Contact person"
              value={tElement.contactPerson}
              onChangeEntity={(v: any) => handleChangeEntitySelector(v, 'contactPerson', 'contactPersonId')}
              searchComponent={generateSearchEntitySelector('people')}
              disabled={disabledAttr('contactPersonId')}
              isReadOnly={isViewMode}
            />
          )}

          {((isViewMode && tElement.owner) || !isViewMode) && (
            <WrapperEntitySelector
              label="Owner"
              value={tElement.owner}
              onChangeEntity={(v: any) => handleChangeEntitySelector(v, 'owner', 'ownerId')}
              searchComponent={generateSearchEntitySelector('user')}
              disabled={disabledAttr('ownerId')}
              isReadOnly={isViewMode}
            />
          )}
          {((isViewMode && tElement.holder) || !isViewMode) && (
            <WrapperEntitySelector
              label="Holder"
              value={tElement.holder}
              onChangeEntity={(v: any) => handleChangeEntitySelector(v, 'holder', 'holderId')}
              searchComponent={generateSearchEntitySelector('user')}
              disabled={disabledAttr('holderId')}
              isReadOnly={isViewMode}
            />
          )}
          {((isViewMode && tElement.nextInLineUser) || !isViewMode) && (
            <WrapperEntitySelector
              label="Next in line user"
              value={tElement.nextInLineUser}
              onChangeEntity={(v: any) => handleChangeEntitySelector(v, 'nextInLineUser', 'nextInLineUserId')}
              searchComponent={generateSearchEntitySelector('user')}
              disabled={disabledAttr('nextInLineUserId')}
              isReadOnly={isViewMode}
            />
          )}
        </>
      </Accordion>
    );
  };

  const renderAccordionLocation = () => {
    if (!isViewMode) return;
    if (tElement.mainFlavor === EFlavors.LOCATION) return;
    if (tElement.defaultLocation === null && tElement.currentLocations.length === 0) return null;

    return (
      <Accordion title="Locations" open={isViewMode}>
        <>
          {((isViewMode && tElement.defaultLocation) || !isViewMode) && (
            <WrapperEntitySelector
              label={'Default location'}
              value={tElement.defaultLocation}
              onChangeEntity={(v: any) => handleChangeEntitySelector(v, 'defaultLocation', 'defaultLocationId')}
              searchComponent={generateSearchEntitySelector('location')}
              isReadOnly={isViewMode}
            />
          )}
          {((isViewMode && tElement.currentLocations) || !isViewMode) && (
            <WrapperEntitySelector
              label={'Current locations'}
              value={tElement.currentLocations}
              fieldToReturn={'customId'}
              //onChangeEntity={(v: any) => handleChangeEntitySelector(v, 'defaultLocation', 'defaultLocationId')}
              onChange={props.onCallbackChange('currentLocations')}
              searchComponent={generateSearchEntitySelector('location')}
              multiple
              isReadOnly={isViewMode}
            />
          )}
        </>
      </Accordion>
    );
  };

  const renderModule1 = () => {
    return (
      <Misc.NewPaperWithPadding>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid size={{ xs: 12 }} sx={{ position: 'sticky', top: '-15px', backgroundColor: 'white', zIndex: 1, display: 'flex' }}>
            <Typography variant="h6" text={isViewMode ? tElement.name : 'Main Information'} />
            <BarcodePrinter value={tElement.getVisualizationID() ?? ''} btnMode />
          </Grid>
          <Grid container size={{ xs: 12 }}>
            <Grid size={{ xs: 9 }}>
              {!isViewMode && (
                <Grid size={{ xs: 12 }}>
                  <TextInput
                    isReadOnly={isViewMode}
                    label="Name"
                    value={tElement.name}
                    onChangeValue={onChangeName}
                    infoText="Blank spaces at the beggining and the end will be trimmed on save"
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12 }}>
                <TextInput
                  isReadOnly={isViewMode}
                  label="Custom ID"
                  value={tElement.customId}
                  onChangeValue={props.onCallbackChange('customId')}
                  infoText="Blank spaces at the beggining and the end will be trimmed on save"
                  type="ID"
                />
              </Grid>
              {tElement.elementTypeId && (
                <Grid size={{ xs: 12 }}>
                  <WrapperEntitySelector
                    label="Item type"
                    multiple={false}
                    value={new CElementType(tElement.elementType)}
                    onChangeEntity={v => changeType(v as CElementType)}
                    searchComponent={generateSearchEntitySelector('itemType')}
                    isReadOnly={true}
                  />
                </Grid>
              )}
            </Grid>
            <Grid size={{ xs: 3 }}>{renderSubModule1()}</Grid>
          </Grid>
          {!(tElement.tagIds && tElement.tagIds.length === 0 && isViewMode) && (
            <Grid size={{ xs: 12 }}>
              <WrapperEntitySelector
                label="Labels"
                value={tElement.tagIds}
                onChange={props.onCallbackChange('tagIds')}
                searchComponent={generateSearchEntitySelector('label')}
                multiple
                isReadOnly={isViewMode}
              />
            </Grid>
          )}
          {!(!tElement.notes && isViewMode) && (
            <Grid size={{ xs: 12 }}>
              <TextInput
                label="Notes"
                value={tElement.notes}
                onChangeValue={props.onCallbackChange('notes')}
                isReadOnly={isViewMode}
                multiline
              />
            </Grid>
          )}
          {renderFlavorComponents()}
          {renderAccordionPeople()}
          {renderAccordionLocation()}
        </Grid>
      </Misc.NewPaperWithPadding>
    );
  };

  const renderStatus = () => {
    // component using immutable internally
    const { elementType, currentStatus } = tElement;
    const currentIndicators = currentStatus != null ? currentStatus.indicators : [];
    const isTemplateStrict = elementType && elementType.templateStatusMode == 'strict';

    return (
      <StatusInput
        mode={props.mode}
        statesList={currentIndicators.map(i => new CStatusIndicator(i))}
        onChangeList={newList => {
          const newStatus = _.clone(currentStatus);
          if (newStatus) newStatus.indicators = newList;
          return props.onCallbackChange('currentStatus')(newStatus);
        }}
        //onLastUpdateClick={handleLastUpdateClick}
        fAllowAdd={!isTemplateStrict}
        //fAllowDelete={!isTemplateStrict}
      />
    );
  };

  const renderLocationItem = () => {
    if (isViewMode) {
      const othersGeometries = tElement.getCurrentLocations;
      return <GeometryDrawer {...props} element={tElement} othersGeometries={othersGeometries} hideInfoSideBar />;
    } else {
      return (
        <GridContainerFullHeight id="locationItem" className="!w-[99.7%]">
          <WrapperEntitySelector
            label={'Default location'}
            value={tElement.defaultLocation}
            onChangeEntity={(v: any) => handleChangeEntitySelector(v, 'defaultLocation', 'defaultLocationId')}
            searchComponent={generateSearchEntitySelector('location')}
          />
          <WrapperEntitySelector
            label={'Current locations'}
            value={tElement.currentLocations}
            fieldToReturn={'customId'}
            onChange={props.onCallbackChange('currentLocations')}
            searchComponent={generateSearchEntitySelector('location')}
            multiple
          />
        </GridContainerFullHeight>
      );
    }
  };

  const renderTabContents = activeTab => {
    let content: any = null;
    switch (activeTab) {
      case 'status':
        content = renderStatus();
        break;
      case 'readings':
        content = <Readings configComponent={{ ...RConfigComponent, fetchURL: `/api/v2/item/${tElement.getID()}/readings` }} />;
        break;
      case 'itemTypeInLocation':
        content = <GTItemTypeInLocation {...props} tElement={tElement} configComponent={GTConfigComponent} />;
        break;
      case 'locationHistory':
        content = <LocationHistoryTab values={tElement} />;
        break;
      case 'locationFlavorLocation':
        content = <GTLocationElement {...props} value={tElement} />;
        break;
      case 'locationItem':
        content = renderLocationItem();
        break;
      case 'itemXtag':
        content = <GTXtagElement />;
        break;
      case 'events':
        content = (
          <Events
            infoDataGrid={[
              'Activities or occurrences that take place physically within the premises or boundaries of the location. This category encompasses a wide range of physical happenings, from routine operations to special gatherings.',
              '\n',
              <b key="example">Example:</b>,
              ' ',
              'Drop a package to a specific location, entering a person in a dangerous zone, or an event indicating a truck is ready to be loaded on a dock. Each of these events directly involves the physical use of the space.',
            ]}
            configComponent={{
              ...EConfigComponent,
              filtersFetchData: {
                itemId: tElement.getID(),
              },
            }}
          />
        );
        break;

      case 'eventEnvolvingLocation':
        content = (
          <Events
            infoDataGrid={[
              `Situations in which the location plays a significant role beyond simply serving as a backdrop or area. This includes cases where the location's status, characteristics, or changes to the location are central to the event and highlight the significance of the contribution or impact of the location to the situation.`,
              '\n',
              <b key="example">Example:</b>,
              ' ',
              'Reaching maximum container capacity in a warehouse, indicating logistical or operational limits, or any alteration that may involve the location as an entity, emphasizing the dynamic role of locations in various contexts.',
            ]}
            configComponent={{
              ...EConfigComponent,
              filtersFetchData: {
                locationId: tElement.getID(),
              },
            }}
          />
        );
        break;

      case 'tasks':
        content = <Tasks configComponent={{ ...TConfigComponent, fetchURL: `/api/tasksByItem/${tElement.getID()}` }} />;
        break;

      default:
        break;
    }
    return content;
  };

  const renderModule2 = () => {
    let listTabs: { label: string; value: string }[] = [];

    listTabs = listTabs.concat([...getListTabsFromItem('module2')]);

    return (
      <Tabs id="module2Tab" slim onChange={handleTabChange('activeTabModule2')} value={state.activeTabModule2} tabs={listTabs}>
        {renderTabContents(state.activeTabModule2)}
      </Tabs>
    );
  };

  const renderModule3 = () => {
    let listTabs: { label: string; value: string }[] = [];

    listTabs = listTabs.concat([...getListTabsFromItem('module3')]);

    return (
      <Tabs
        id="module3Tab"
        sticky={-10}
        slim
        onChange={handleTabChange('activeTabModule3')}
        value={state.activeTabModule3}
        tabs={listTabs}
      >
        {renderTabContents(state.activeTabModule3)}
      </Tabs>
    );
  };
  //#endregion

  return (
    <Grid container size={{ xs: 12 }} columnSpacing={1} rowSpacing={3} id="general-tab" pb={2}>
      <Grid size={{ xs: 12, lg: 6 }} style={{ height: isViewMode ? '48vh' : '77vh' }}>
        {renderModule1()}
      </Grid>
      <Grid size={{ xs: 12, lg: 6 }} style={{ height: isViewMode ? '48vh' : '77vh' }}>
        <Misc.NewPaperWithPadding>{renderModule2()}</Misc.NewPaperWithPadding>
      </Grid>
      <Grid size={12} style={{ height: '48vh', paddingTop: '0!important' }}>
        <Misc.NewPaperWithPadding>{renderModule3()}</Misc.NewPaperWithPadding>
      </Grid>
    </Grid>
  );
};

export default NewGeneralTab;
